/**
 * Add/Edit Credit-bucket dialog (DNKADM-0052 U2) — a MUI `Dialog` that
 * creates/updates a `v2_credit_bucket` via `createCreditBucket` /
 * `updateCreditBucket` (handoff §5.4).
 *
 * Fields: **name** (required), optional **description**, the optional
 * **`ghlCustomFieldKey`** — the explicit contact-system mirror target for this
 * bucket's balance — and the optional **price per credit** (DNKADM-0237). Per the
 * white-label guardrail (§4.0) `ghlCustomFieldKey` is DATA: its INPUT is labeled
 * brand-neutrally via `CONTACT_SYSTEM_FIELD_KEY_LABEL` ("Contact-system field
 * key"), never "GHL …". `isActive` is NOT set here — activation is the list's
 * deactivate/reactivate affordance (§4.3).
 *
 * **Price per credit (DNKADM-0237).** The money input follows the DNKADM-0208
 * no-show-fee pattern (tenant-currency adornment, 2dp, never a hardcoded `$`).
 * Blank means `null` — the documented "unpriced / not sellable" state — and
 * clearing the field SENDS `null` on save. A non-blank value must be `> 0`; the
 * server's own rejection (its rounding rule is not duplicated client-side)
 * surfaces inline like any validation `400`. When the dialog opens on an existing
 * bucket with NO price, a **suggested** price per credit is derived from the
 * bucket's own service pricing (`variation.price / variation.creditCost`, taking
 * the variation whose `creditCost` is closest to 1 per service) and offered as a
 * one-tap "Use suggested" prefill. The suggestion is PRESENTATIONAL: it is never
 * saved unless accepted, never recomputed once a value is stored, and it is
 * suppressed entirely when it would be ambiguous (services with differing implied
 * values), unavailable (no usable `creditCost > 0`), or the read fails — a wrong
 * money suggestion is worse than none.
 *
 * **Unit (DNKADM-0319 / DNKAPI-0484).** A three-option select — Hours / Sessions /
 * Credits — for the noun a member reads the quantity in ("1.5 hours", not "1.5
 * Club Fittings"). Create defaults to Credits (the server default); edit pre-fills
 * from the loaded bucket. The value is ALWAYS sent explicitly on every save: on
 * PUT the server treats an absent/blank `unit` as *unchanged* (unlike every other
 * field's full-replace), so a blank could never reset it and would silently no-op.
 *
 * Client validation blocks Save while the name is empty or the price is invalid.
 * On a successful create/update the parent **refetches** (the caller's `onSaved`);
 * a validation `400` / a unique-name `409` renders INLINE, anything else raises an
 * error toast (`classifyFormError`).
 */
import { useCallback, useId, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import {
  CREDIT_UNITS,
  DEFAULT_CREDIT_UNIT,
  createCreditBucket,
  creditUnitLabel,
  updateCreditBucket,
  type CreditBucket,
  type CreditBucketInput,
  type CreditUnit,
} from '@/api/creditBuckets';
import { listServices, listVariations } from '@/api/services';
import { useResource } from '@/hooks/useResource';
import { formatMoney } from '@/utils/v2Format';
import { CONTACT_SYSTEM_FIELD_KEY_LABEL } from '@/utils/whitelabel';
import type { ShowToast } from '@/components/toastContext';
import { classifyFormError } from '@/api/screenError';

export interface CreditBucketDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** The bucket being edited, or `null` to add a new one. */
  bucket: CreditBucket | null;
  /** The tenant ISO-4217 currency code for the money input, or null while it loads. */
  currency: string | null;
  /** Close the dialog without saving. */
  onClose: () => void;
  /** Called after a successful create/update so the caller can refetch the list. */
  onSaved: () => void;
  /** Raise a non-blocking toast (upstream-error). */
  showToast: ShowToast;
}

export function CreditBucketDialog(props: CreditBucketDialogProps) {
  // Remount the inner form per open / per edited bucket so its inputs reseed.
  if (!props.open) return null;
  return <CreditBucketDialogForm key={props.bucket?.id ?? 'new'} {...props} />;
}

/** Trim a string and return `undefined` when empty (omit the optional key). */
function optional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * Parse the price-per-credit text (DNKADM-0208 money-input pattern, tightened to
 * the server's `> 0` rule): blank → `null` (unpriced / not sellable); otherwise a
 * positive amount with up to 2 decimal places.
 */
function parseOptionalPositiveMoney(
  text: string,
): { valid: true; value: number | null } | { valid: false } {
  const trimmed = text.trim();
  if (trimmed === '') return { valid: true, value: null };
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return { valid: false };
  const value = Number(trimmed);
  return Number.isFinite(value) && value > 0 ? { valid: true, value } : { valid: false };
}

/**
 * Derive the SUGGESTED price per credit for an existing UNPRICED bucket from its
 * own service pricing (DNKADM-0237 U2), or `null` when no trustworthy suggestion
 * exists. Per service pointing at the bucket, the implied per-credit value is
 * `variation.price / variation.creditCost` of the variation whose `creditCost` is
 * closest to 1 (2dp). Suppressed (`null`) when: the bucket is new or already
 * priced; no service points at it; no variation has a usable `creditCost > 0` and
 * `price > 0`; services disagree on the implied value (ambiguous); or any read
 * fails. Failures are SILENT — the operator simply gets no suggestion.
 */
async function fetchSuggestedPricePerCredit(bucket: CreditBucket | null): Promise<number | null> {
  if (bucket === null || bucket.pricePerCredit != null) return null;
  try {
    const services = (await listServices()).filter((s) => s.creditBucketId === bucket.id);
    if (services.length === 0) return null;
    const implied = (
      await Promise.all(
        services.map(async (service) => {
          const usable = (await listVariations(service.id)).filter(
            (v) => v.creditCost > 0 && v.price > 0,
          );
          if (usable.length === 0) return null;
          const closest = usable.reduce((a, b) =>
            Math.abs(b.creditCost - 1) < Math.abs(a.creditCost - 1) ? b : a,
          );
          return Math.round((closest.price / closest.creditCost) * 100) / 100;
        }),
      )
    ).filter((v): v is number => v !== null && v > 0);
    if (implied.length === 0) return null;
    // Ambiguous — services imply DIFFERENT per-credit values: no suggestion.
    if (implied.some((v) => v !== implied[0])) return null;
    return implied[0]!;
  } catch {
    return null;
  }
}

function CreditBucketDialogForm({
  bucket,
  currency,
  onClose,
  onSaved,
  showToast,
}: Omit<CreditBucketDialogProps, 'open'>) {
  const ids = useId();
  const isEdit = bucket !== null;

  const [name, setName] = useState(bucket?.name ?? '');
  const [description, setDescription] = useState(bucket?.description ?? '');
  const [fieldKey, setFieldKey] = useState(bucket?.ghlCustomFieldKey ?? '');
  const [price, setPrice] = useState(
    bucket?.pricePerCredit == null ? '' : bucket.pricePerCredit.toFixed(2),
  );
  // Create defaults to the server default; edit pre-fills from the loaded bucket
  // (the parser always assigns `unit`, so an edit never starts blank).
  const [unit, setUnit] = useState<CreditUnit>(bucket?.unit ?? DEFAULT_CREDIT_UNIT);

  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [priceError, setPriceError] = useState<string | undefined>(undefined);
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // The derived suggestion (U2) — computed once per dialog open, only for an
  // existing unpriced bucket (the fetcher no-ops otherwise). Loading and failure
  // states render nothing: the affordance appears only with a trustworthy value.
  const suggestion = useResource(
    useCallback(() => fetchSuggestedPricePerCredit(bucket), [bucket]),
  );
  const suggestedPrice = suggestion.status === 'ready' ? suggestion.data : null;

  function validate(): CreditBucketInput | null {
    const trimmedName = name.trim();
    const parsedPrice = parseOptionalPositiveMoney(price);
    setNameError(trimmedName === '' ? 'Enter a bucket name.' : undefined);
    setPriceError(
      parsedPrice.valid
        ? undefined
        : 'Enter an amount greater than 0 (up to 2 decimal places), or leave blank.',
    );
    if (trimmedName === '' || !parsedPrice.valid) return null;

    // `pricePerCredit` is ALWAYS set: a blank field is an explicit `null`
    // (unpriced / not sellable), never `0`, never an omitted key. `unit` is ALWAYS
    // set too — on PUT an absent unit is "unchanged", never a reset (DNKADM-0319).
    const input: CreditBucketInput = {
      name: trimmedName,
      pricePerCredit: parsedPrice.value,
      unit,
    };
    const desc = optional(description);
    if (desc !== undefined) input.description = desc;
    const key = optional(fieldKey);
    if (key !== undefined) input.ghlCustomFieldKey = key;
    return input;
  }

  async function handleSave() {
    setServerError(null);
    const input = validate();
    if (!input) return;
    setSaving(true);
    try {
      if (isEdit && bucket) await updateCreditBucket(bucket.id, input);
      else await createCreditBucket(input);
      // DNKADM-0320: confirm the save (after the write resolves; one toast per action).
      showToast(isEdit ? 'Credit bucket saved' : 'Credit bucket added', 'success');
      onSaved();
    } catch (err) {
      const view = classifyFormError(
        err,
        'The credit bucket couldn’t be saved. Please try again.',
      );
      if (view.kind === 'inline') setServerError(view.message);
      else showToast(view.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  const titleId = `${ids}-title`;
  const unitLabelId = `${ids}-unit-label`;
  const currencyAdornment = currency ? (
    <InputAdornment position="start">{currency}</InputAdornment>
  ) : undefined;

  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>{isEdit ? 'Edit credit bucket' : 'Add credit bucket'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            size="small"
            required
            error={Boolean(nameError)}
            helperText={nameError}
            slotProps={{ htmlInput: { 'aria-label': 'Bucket name' } }}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={saving}
            size="small"
            multiline
            minRows={2}
            slotProps={{ htmlInput: { 'aria-label': 'Description' } }}
          />
          <TextField
            label={CONTACT_SYSTEM_FIELD_KEY_LABEL}
            value={fieldKey}
            onChange={(e) => setFieldKey(e.target.value)}
            disabled={saving}
            size="small"
            helperText="Where this bucket’s balance is mirrored in the contact system."
            slotProps={{ htmlInput: { 'aria-label': CONTACT_SYSTEM_FIELD_KEY_LABEL } }}
          />
          <FormControl size="small" fullWidth disabled={saving}>
            <InputLabel id={unitLabelId}>Unit</InputLabel>
            <Select
              labelId={unitLabelId}
              label="Unit"
              value={unit}
              onChange={(e) => setUnit(e.target.value as CreditUnit)}
              slotProps={{ input: { 'aria-label': 'Unit' } }}
            >
              {CREDIT_UNITS.map((u) => (
                <MenuItem key={u} value={u}>
                  {creditUnitLabel(u)}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              How the quantity reads to a member — “1.5 hours” rather than “1.5 Club Fittings”.
            </FormHelperText>
          </FormControl>
          <Box>
            <TextField
              label="Price per credit"
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              disabled={saving}
              size="small"
              fullWidth
              error={Boolean(priceError)}
              helperText={
                priceError ??
                'Members can buy credits from this bucket in the app only when it has a price. Leave blank to keep it unsellable.'
              }
              slotProps={{
                htmlInput: {
                  'aria-label': 'Price per credit',
                  min: 0,
                  step: '0.01',
                  inputMode: 'decimal',
                },
                ...(currencyAdornment ? { input: { startAdornment: currencyAdornment } } : {}),
              }}
            />
            {suggestedPrice != null && (
              <Button
                size="small"
                onClick={() => setPrice(suggestedPrice.toFixed(2))}
                disabled={saving}
                sx={{ mt: 0.5 }}
              >
                Use suggested:{' '}
                {currency ? formatMoney(suggestedPrice, currency) : suggestedPrice.toFixed(2)}
              </Button>
            )}
          </Box>

          {serverError && (
            <Alert severity="error" role="alert">
              {serverError}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save bucket' : 'Add bucket'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
